import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Calendar, Clock, Users, Briefcase, Car, 
  DollarSign, Send, CheckCircle, XCircle, Star, Loader2,
  AlertCircle, TrendingUp, Navigation, Plane, Filter, Search,
  ArrowUpDown, RotateCcw, ChevronLeft, ChevronRight, ExternalLink,
  Baby, SlidersHorizontal, FileText, Upload, Camera, Shield, X,
  User, Lock, LogOut, ChevronDown, Settings
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { useTranslation } from '../translations';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DocumentUploadModal } from '../components/driver/DocumentUploadModal';
import { OfferModal } from '../components/driver/OfferModal';
import { DriverBookings } from '../components/driver/DriverBookings';
import { ProfileSettingsModal } from '../components/ProfileSettingsModal';
import { VehicleManager } from '../components/Driver/VehicleManager';
import { DriverProfileTab } from '../components/Driver/DriverProfileTab';
import { DriverEarningsTab } from '../components/driver/DriverEarningsTab';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom markers for map
const startIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#FF6B00" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#FF6B00" font-size="10" font-weight="bold">A</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
});

const endIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#000000" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#000000" font-size="10" font-weight="bold">B</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
});

// Component to fit map bounds
function FitBounds({ from, to }) {
  const map = useMap();
  useEffect(() => {
    if (from && to) {
      const bounds = L.latLngBounds([
        [from.lat, from.lng],
        [to.lat, to.lng]
      ]);
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [map, from, to]);
  return null;
}

// Vehicle type icons
const VehicleIcons = {
  economy: '🚗',
  comfort: '🚙',
  business: '🚘',
  premium: '🏎️',
  vip: '👑',
  suv: '🚙',
  van: '🚐',
  minibus: '🚌',
  bus: '🚌',
  any: '🚗',
};

export default function DriverDashboard() {
  const navigate = useNavigate();
  const { user, token, language, loadFromStorage, logout } = useAuthStore();
  const { t } = useTranslation(language);
  const [driverProfile, setDriverProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  const [requests, setRequests] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerNotes, setOfferNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [suggestedPrice, setSuggestedPrice] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [searchQuery, setSearchQuery] = useState('');
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [completingTransfer, setCompletingTransfer] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docStatus, setDocStatus] = useState(null);
  const [pendingOfferRequest, setPendingOfferRequest] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const profileMenuRef = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => { document.title = 'Fahrer-Dashboard & Aufträge | 366Transfer Fahrer'; }, []);

  // Close profile menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize: load auth from storage and immediately fetch data if token exists
  // We read directly from localStorage here to avoid a race condition between
  // Zustand's set() propagation and React's setInitialized() — on slow mobile
  // browsers this race caused a brief render with token=null, triggering the
  // "Kein Fahrerprofil" empty state before the real dashboard appeared.
  useEffect(() => {
    loadFromStorage();
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      loadData();
    } else {
      setLoading(false);
    }
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect admin users to admin dashboard
  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  const loadData = async () => {
    // Load critical data first (profile + requests) so the UI can render quickly
    try {
      const [profileRes, requestsRes] = await Promise.all([
        api.get('/driver/profile').catch(() => ({ data: null })),
        api.get('/driver/requests').catch(() => ({ data: [] })),
      ]);
      setDriverProfile(profileRes.data);
      setRequests(requestsRes.data || []);
    } catch (err) {
      console.error('Error loading critical driver data:', err);
    } finally {
      setLoading(false); // Release the spinner as soon as the main data is ready
    }

    // Load secondary data in the background — they update tab counters/badges
    // but don't block the dashboard from rendering.
    Promise.all([
      api.get('/driver/my-offers').catch(() => ({ data: [] })),
      api.get('/driver/bookings').catch(() => ({ data: [] })),
      api.get('/driver/documents-status').catch(() => ({ data: null })),
    ]).then(([offersRes, bookingsRes, docStatusRes]) => {
      setMyOffers(offersRes.data || []);
      setBookings(bookingsRes.data || []);
      if (docStatusRes.data) setDocStatus(docStatusRes.data);
    });
  };

  const getPriceSuggestion = async (request) => {
    try {
      const res = await api.post('/ai/price-suggestion', {
        distance_km: request.distance_km || 50,
        duration_minutes: request.duration_minutes || 60,
        vehicle_type: request.preferred_vehicle_type || 'economy',
        passengers: (request.adults || 1) + (request.children || 0)
      });
      setSuggestedPrice(res.data);
      setOfferPrice(res.data.suggested_price?.toString() || '');
    } catch (err) {
      console.error('Error getting price suggestion:', err);
    }
  };

  const handleSelectRequest = async (request) => {
    // Check document status first — but allow approved drivers through
    // (Admin can manually approve a driver without all docs).
    const isApproved = docStatus?.driver_status === 'approved';
    if (docStatus && !docStatus.complete && !isApproved) {
      setPendingOfferRequest(request);
      setShowDocModal(true);
      return;
    }
    if (docStatus && docStatus.driver_status === 'pending') {
      alert(t('driver_pending_desc'));
      return;
    }
    openOfferModal(request);
  };

  const openOfferModal = async (request) => {
    setSelectedRequest(request);
    setOfferPrice('');
    setOfferNotes('');
    setSuggestedPrice(null);
    setRouteCoordinates([]);
    getPriceSuggestion(request);
    
    // Fetch route from OSRM
    if (request.pickup_location && request.dropoff_location) {
      try {
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${request.pickup_location.lng},${request.pickup_location.lat};${request.dropoff_location.lng},${request.dropoff_location.lat}?overview=full&geometries=geojson`
        );
        const data = await response.json();
        
        if (data.routes && data.routes[0]) {
          const coords = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
          setRouteCoordinates(coords);
        }
      } catch (error) {
        console.error('Error fetching route:', error);
        setRouteCoordinates([
          [request.pickup_location.lat, request.pickup_location.lng],
          [request.dropoff_location.lat, request.dropoff_location.lng]
        ]);
      }
    }
  };

  const handleDocModalComplete = (docStatusData) => {
    setShowDocModal(false);
    if (pendingOfferRequest) {
      if (docStatusData.driver_status === 'pending') {
        alert(t('driver_pending_desc'));
      } else {
        openOfferModal(pendingOfferRequest);
      }
      setPendingOfferRequest(null);
    }
  };

  const handleSubmitOffer = async () => {
    if (!offerPrice || parseFloat(offerPrice) <= 0) {
      alert(t('driver_offer_invalid_price'));
      return;
    }
    if (!selectedVehicleId) {
      alert('Bitte wählen Sie ein Fahrzeug aus.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/driver/offer', {
        transfer_id: selectedRequest.id,
        price: parseFloat(offerPrice),
        currency: 'EUR',
        notes: offerNotes || null,
        vehicle_id: selectedVehicleId
      });

      alert(t('driver_offer_success'));
      setSelectedRequest(null);
      setSelectedVehicleId(null);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'vehicle_not_approved') {
        alert('Dieses Fahrzeug ist noch nicht vom Admin freigegeben.');
      } else if (detail === 'vehicle_documents_incomplete') {
        alert('Die Dokumente für dieses Fahrzeug sind unvollständig.');
      } else {
        alert(detail || t('common_error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteTransfer = async (transferId) => {
    if (!window.confirm(t('driver_complete_confirm'))) return;
    setCompletingTransfer(transferId);
    try {
      await api.post(`/transfers/${transferId}/complete`);
      alert(t('driver_complete_success'));
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || t('common_error'));
    } finally {
      setCompletingTransfer(null);
    }
  };

  const openInGoogleMaps = (lat, lng, address) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const handleProfileUpdated = useCallback((data) => {
    if (data.user) {
      loadFromStorage();
    }
    if (data.driver_profile) {
      setDriverProfile(data.driver_profile);
    }
    if (data.requires_reapproval) {
      loadData();
    }
  }, [loadFromStorage]);

  const formatLocation = (location) => {
    if (!location) return '-';
    
    // If it's coordinates (no readable address)
    if (location.address && location.address.match(/^\d+\.\d+,\s*\d+\.\d+$/)) {
      return `${location.lat?.toFixed(6)}, ${location.lng?.toFixed(6)}`;
    }
    
    // Return short address
    const parts = location.address?.split(',') || [];
    return parts.slice(0, 2).join(', ') || `${location.lat?.toFixed(6)}, ${location.lng?.toFixed(6)}`;
  };

  const formatDate = (dateStr) => {
    // Display the local pickup time exactly as stored — no TZ conversion.
    // Customers enter local time at the pickup location and we want it to
    // appear identically for everyone.
    if (!dateStr) return '';
    const s = String(dateStr).replace(/Z$/, '').replace(/[+-]\d{2}:?\d{2}$/, '');
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
    if (!m) return dateStr;
    const [, y, mo, d, hh = '00', mm = '00'] = m;
    const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    const wd = new Date(`${y}-${mo}-${d}T00:00:00`).getDay();
    return `${weekdays[wd]}, ${parseInt(d, 10)}. ${months[parseInt(mo, 10) - 1]}, ${hh}:${mm}`;
  };

  // Filter and sort requests
  const filteredRequests = requests.filter(r => {
    if (activeTab === 'new' && r.already_offered) return false;
    if (activeTab === 'with_offers' && !r.already_offered) return false;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        r.pickup_location?.address?.toLowerCase().includes(query) ||
        r.dropoff_location?.address?.toLowerCase().includes(query) ||
        r.flight_number?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Loading state
  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-4" />
          <p className="text-slate-500">{t('common_loading')}</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user || !token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-md">
          <Car size={48} className="mx-auto text-orange-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t('driver_login_required')}</h2>
          <p className="text-slate-500 mb-4">{t('driver_login_desc')}</p>
          <a href="/" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
            {t('driver_login_btn')}
          </a>
        </div>
      </div>
    );
  }

  // Not a driver or rejected
  if (!driverProfile || driverProfile.status === 'rejected' || driverProfile.status === 'blocked') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-md">
          {!driverProfile ? (
            <>
              <AlertCircle size={48} className="mx-auto text-orange-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-800 mb-2">{t('driver_no_profile')}</h2>
              <p className="text-slate-500 mb-4">{t('driver_no_profile_desc')}</p>
              <a href="/driver/register" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
                {t('driver_register_btn')}
              </a>
            </>
          ) : (
            <>
              <XCircle size={48} className="mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-800 mb-2">{t('driver_rejected_title')}</h2>
              <p className="text-slate-500 mb-4">{t('driver_rejected_desc')}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Guard: never render driver dashboard for admin users
  if (user?.role === 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="driver-dashboard">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="flex items-baseline gap-0.5">
              <span className="text-2xl font-extrabold text-orange-500">366</span>
              <span className="text-sm font-semibold text-slate-800">Transfer</span>
            </a>
            <span className="text-sm text-slate-500">{t('driver_portal')}</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowDocModal(true)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                docStatus?.complete
                  ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 animate-pulse'
              }`}
              data-testid="docs-btn"
            >
              <FileText size={16} />
              {t('driver_docs_btn')}
              {!docStatus?.complete && <span className="w-2 h-2 rounded-full bg-amber-500" />}
            </button>
            <span className="text-sm text-slate-500">
              {driverProfile?.vehicles?.length ? `${driverProfile.vehicles.length} Fahrzeug${driverProfile.vehicles.length > 1 ? 'e' : ''}` : ''}
            </span>
            {/* Profile dropdown */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-full hover:bg-emerald-100 transition-colors"
                data-testid="profile-menu-btn"
              >
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-sm font-medium text-emerald-700">{user?.name}</span>
                <ChevronDown size={14} className={`text-emerald-600 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
              </button>
              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-50" data-testid="profile-dropdown">
                  <button
                    onClick={() => { setShowProfileMenu(false); setShowProfileSettings(true); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="profile-edit-btn"
                  >
                    <Settings size={16} className="text-slate-400" />
                    {t('profile_settings')}
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    onClick={() => { setShowProfileMenu(false); logout(); navigate('/'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut size={16} className="text-red-400" />
                    {t('profile_logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Status Banner */}
        {driverProfile?.status === 'pending' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center gap-3" data-testid="pending-banner">
            <Loader2 size={18} className="text-yellow-600 animate-spin flex-shrink-0" />
            <p className="text-sm text-yellow-800">{t('driver_pending_desc')}</p>
          </div>
        )}
        {docStatus && !docStatus.complete && docStatus.driver_status !== 'approved' && (
          <div
            className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => setShowDocModal(true)}
            data-testid="docs-incomplete-banner"
          >
            <div className="flex items-center gap-3">
              <FileText size={18} className="text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">{t('driver_docs_incomplete')} — {docStatus.missing?.length} {t('driver_docs_required')}</p>
            </div>
            <span className="text-amber-600 text-sm font-medium">{t('driver_docs_upload')} &rarr;</span>
          </div>
        )}
        {/* Tabs and Filters */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setActiveTab('new'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'new'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="tab-new"
            >
              {t('driver_tab_new')}
            </button>
            <button
              onClick={() => { setActiveTab('with_offers'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'with_offers'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="tab-with-offers"
            >
              {t('driver_tab_with_offers')}
            </button>
            <button
              onClick={() => { setActiveTab('bookings'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'bookings'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="tab-bookings"
            >
              {t('driver_tab_bookings')} ({bookings.length})
            </button>
            <button
              onClick={() => { setActiveTab('vehicles'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'vehicles'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="tab-vehicles"
            >
              {t('driver_tab_vehicles') || 'Fahrzeuge'} ({driverProfile?.vehicles?.length || 0})
            </button>
            <button
              onClick={() => { setActiveTab('earnings'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'earnings'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="tab-earnings"
            >
              Einnahmen
            </button>
            <button
              onClick={() => { setActiveTab('profile'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="tab-profile"
            >
              {t('driver_tab_profile') || 'Mein Profil'}
            </button>
            <div className="w-px h-6 bg-slate-200 mx-2" />
            <button className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
              <SlidersHorizontal size={16} />
              {t('driver_sort_created')}
            </button>
            <button className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
              <Filter size={16} />
              {t('driver_filter')}
            </button>
          </div>
          <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 w-64">
            <Search size={16} className="text-slate-400" />
            <input
              type="text"
              placeholder={t('driver_search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent outline-none text-sm flex-1"
            />
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'profile' ? (
          <DriverProfileTab driverProfile={driverProfile} user={user} onRefresh={loadData} t={t} />
        ) : activeTab === 'earnings' ? (
          <DriverEarningsTab t={t} formatDate={formatDate} />
        ) : activeTab === 'vehicles' ? (
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <VehicleManager
              vehicles={driverProfile?.vehicles || []}
              onRefresh={loadData}
              t={t}
            />
          </div>
        ) : activeTab === 'bookings' ? (
          <DriverBookings
            t={t}
            bookings={bookings}
            formatDate={formatDate}
            formatLocation={formatLocation}
            onComplete={handleCompleteTransfer}
            completingTransfer={completingTransfer}
          />
        ) : (
        /* Requests Table/Cards */
        <>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Desktop Table - hidden on mobile */}
          <table className="w-full hidden lg:table">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_id')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_ride_date')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-12">{t('driver_col_type')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_distance')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_from')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_to')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_passengers')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_transport')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('driver_col_your_offer')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedRequests.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-slate-400">
                    <Navigation size={32} className="mx-auto mb-2 opacity-50" />
                    <p>{t('driver_no_requests')}</p>
                  </td>
                </tr>
              ) : (
                paginatedRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{request.id?.slice(0, 8)}</p>
                        <p className="text-xs text-slate-400">{t('driver_created')} {request.created_at ? new Date(request.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{formatDate(request.pickup_date)}</p>
                        <p className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded inline-block mt-1">
                          {request.duration_minutes ? `~${request.duration_minutes} min` : '15 min'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {request.has_return ? <RotateCcw size={18} className="text-orange-500" /> : <MapPin size={18} className="text-slate-400" />}
                    </td>
                    <td className="px-4 py-4"><p className="text-sm font-medium text-slate-800">{request.distance_km || '?'} km</p></td>
                    <td className="px-4 py-4 max-w-[200px]">
                      <div className="flex items-start gap-2">
                        <button onClick={() => openInGoogleMaps(request.pickup_location?.lat, request.pickup_location?.lng)} className="text-blue-500 hover:text-blue-700 shrink-0 mt-0.5"><ExternalLink size={14} /></button>
                        <p className="text-sm text-slate-700 truncate">{formatLocation(request.pickup_location)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 max-w-[200px]">
                      <div className="flex items-start gap-2">
                        <button onClick={() => openInGoogleMaps(request.dropoff_location?.lat, request.dropoff_location?.lng)} className="text-blue-500 hover:text-blue-700 shrink-0 mt-0.5"><ExternalLink size={14} /></button>
                        <p className="text-sm text-slate-700 truncate">{formatLocation(request.dropoff_location)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="flex items-center gap-1 text-sm text-slate-700"><Users size={14} className="text-slate-400" /> ×{request.adults || 1}</span>
                      {request.children > 0 && <span className="flex items-center gap-1 text-sm text-slate-700"><Baby size={14} className="text-slate-400" /> ×{request.children}</span>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{VehicleIcons[request.preferred_vehicle_type] || '🚗'}</span>
                        <span className="text-sm text-slate-600 capitalize">{request.preferred_vehicle_type || 'Any'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {request.already_offered ? (
                        <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">{t('driver_offered')}</span>
                      ) : (
                        <button onClick={() => handleSelectRequest(request)} className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors" data-testid={`offer-btn-${request.id}`}>{t('driver_offer_price_btn')}</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Mobile Cards - shown only on mobile */}
          <div className="lg:hidden">
            {paginatedRequests.length === 0 ? (
              <div className="px-4 py-12 text-center text-slate-400">
                <Navigation size={32} className="mx-auto mb-2 opacity-50" />
                <p>{t('driver_no_requests')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {paginatedRequests.map((request) => (
                  <div key={request.id} className="p-4" data-testid={`mobile-request-${request.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-400">#{request.id?.slice(0, 8)}</span>
                      <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded font-medium">
                        {request.duration_minutes ? `~${request.duration_minutes} min` : ''}
                      </span>
                    </div>
                    <p className="font-bold text-slate-800 mb-2">{formatDate(request.pickup_date)}</p>
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-start gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                        <p className="text-sm text-slate-700 line-clamp-1">{request.pickup_location?.address}</p>
                        <button onClick={() => openInGoogleMaps(request.pickup_location?.lat, request.pickup_location?.lng)} className="text-blue-500 shrink-0"><ExternalLink size={14} /></button>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                        <p className="text-sm text-slate-700 line-clamp-1">{request.dropoff_location?.address}</p>
                        <button onClick={() => openInGoogleMaps(request.dropoff_location?.lat, request.dropoff_location?.lng)} className="text-blue-500 shrink-0"><ExternalLink size={14} /></button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span>{request.distance_km || '?'} km</span>
                      <span className="flex items-center gap-1"><Users size={12} /> {request.adults || 1} Pers.</span>
                      <span className="flex items-center gap-1">{VehicleIcons[request.preferred_vehicle_type] || '🚗'} {request.preferred_vehicle_type || 'economy'}</span>
                      {request.flight_number && <span className="text-blue-500">{request.flight_number}</span>}
                    </div>
                    {request.already_offered ? (
                      <span className="block text-center px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">{t('driver_offered')}</span>
                    ) : (
                      <button onClick={() => handleSelectRequest(request)} className="w-full py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-semibold hover:bg-emerald-600 transition-colors" data-testid={`mobile-offer-btn-${request.id}`}>{t('driver_offer_price_btn')}</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={18} />
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-orange-500 text-white'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            {totalPages > 5 && <span className="text-slate-400">...</span>}
            {totalPages > 5 && (
              <button
                onClick={() => setCurrentPage(totalPages)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === totalPages
                    ? 'bg-orange-500 text-white'
                    : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {totalPages}
              </button>
            )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
        {/* End of Requests Table conditional */}
        </>
        )}

        {/* Stats Summary */}
        <div className="mt-6 flex gap-4 text-sm text-slate-500">
          <span>{filteredRequests.filter(r => !r.already_offered).length} {t('driver_new_requests')}</span>
          <span>•</span>
          <span>{myOffers.length} {t('driver_active_offers_count')}</span>
          <span>•</span>
          <span>{bookings.length} {t('driver_bookings_count')}</span>
        </div>
      </div>

      {/* Offer Modal */}
      {selectedRequest && (
        <OfferModal
          t={t}
          request={selectedRequest}
          suggestedPrice={suggestedPrice}
          offerPrice={offerPrice}
          setOfferPrice={setOfferPrice}
          offerNotes={offerNotes}
          setOfferNotes={setOfferNotes}
          submitting={submitting}
          routeCoordinates={routeCoordinates}
          onSubmit={handleSubmitOffer}
          onClose={() => { setSelectedRequest(null); setSelectedVehicleId(null); }}
          formatLocation={formatLocation}
          openInGoogleMaps={openInGoogleMaps}
          vehicles={driverProfile?.vehicles || []}
          selectedVehicleId={selectedVehicleId}
          setSelectedVehicleId={setSelectedVehicleId}
        />
      )}

      {/* Document Upload Modal */}
      {showDocModal && (
        <DocumentUploadModal
          t={t}
          docStatus={docStatus}
          onClose={() => { setShowDocModal(false); setPendingOfferRequest(null); }}
          onComplete={handleDocModalComplete}
          onDocStatusChange={setDocStatus}
        />
      )}

      {/* Profile Settings Modal */}
      {showProfileSettings && (
        <ProfileSettingsModal
          t={t}
          driverProfile={driverProfile}
          onClose={() => setShowProfileSettings(false)}
          onProfileUpdated={handleProfileUpdated}
        />
      )}
    </div>
  );
}
